import { UserInfoWrapper } from "@/components/UserInfo/UserInfoWrapper";
import { useProjectRoleContext } from "@/containers/Admin/ProjectRoles/ProjectRoleProvider";
import { AddLimitedListUserModal } from "@/containers/Admin/ProjectRoles/components/RegionalRole/Modals";
import { nameof } from "@/helpers";
import { IUser } from "@/interfaces";
import { ILimitedListUser } from "@/interfaces/Project/Roles/ILimitedListUser";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import { useGetUsersByIdsMutation } from "@/services";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Table, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IProps {
  regionRole: IRegionalProjectRole;
}

export const LimitedListTab = ({ regionRole }: IProps) => {
  const intl = useIntl();

  const { updateRegionalRole } = useProjectRoleContext();
  const [getUsersByIds, { data: usersByIds, isLoading }] =
    useGetUsersByIdsMutation();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [limitedList, setLimitedList] = useState<ILimitedListUser[]>([]);

  useEffect(() => {
    if (!regionRole?.limitedListUsers) return;

    setLimitedList(regionRole.limitedListUsers);
  }, [regionRole?.limitedListUsers]);

  useEffect(() => {
    if (!limitedList) return;

    const userIds = limitedList.map((user) => user.userId);
    getUsersByIds(userIds);
  }, [limitedList, getUsersByIds]);

  const addUser = (newUser: ILimitedListUser): void => {
    const updatedRegionRole = { ...regionRole };
    updatedRegionRole.limitedListUsers = [newUser, ...limitedList];

    updateRegionalRole(updatedRegionRole);
    setModalOpen(false);
  };

  const deleteUser = (id: string): void => {
    const updatedRegionRole = { ...regionRole };
    updatedRegionRole.limitedListUsers = limitedList.filter(
      (user) => user?.userId !== id
    );

    updateRegionalRole(updatedRegionRole);
  };

  const handleAddAnother = (newUser: ILimitedListUser): void => {
    addUser(newUser);
    setModalOpen(true);
  };

  const columns: ColumnsType<ILimitedListUser> = [
    {
      title: intl.formatMessage({ id: "projectRoles.name" }),
      dataIndex: nameof<ILimitedListUser>("userId"),
      render: (userId) => {
        const user = usersByIds?.find((x: IUser) => x.id === userId);
        return (
          <UserInfoWrapper user={user}>{user?.displayName}</UserInfoWrapper>
        );
      },
    },
    {
      title: intl.formatMessage({ id: "projectRoles.action" }),
      dataIndex: "",
      render: (limitedListUser) => (
        <Button
          data-test-id={`limited-list-delete-${limitedListUser.userId}`}
          type="link"
          onClick={(): void => deleteUser(limitedListUser.userId)}
          icon={<FontAwesomeIcon icon={faTrash} />}
        ></Button>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" className="pl-1">
        <Col>
          <Typography.Title level={5}>
            {intl.formatMessage({ id: "projectRoles.limitedListUsers" })}
          </Typography.Title>
        </Col>
        <Col>
          <Button
            data-test-id="add-limited-list-user"
            onClick={(): void => setModalOpen(true)}
          >
            {intl.formatMessage({ id: "projectRoles.addUser" })}
          </Button>
        </Col>
      </Row>
      <Table
        loading={isLoading}
        columns={columns}
        dataSource={limitedList}
        rowKey={({ userId }) => userId}
      />
      <AddLimitedListUserModal
        regionRole={regionRole}
        open={modalOpen}
        onClose={(): void => setModalOpen(false)}
        onAddAnother={handleAddAnother}
        onOk={addUser}
      />
    </>
  );
};
